import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Building2, Shield, FileText, Scale, Wallet, Gavel, Globe, Mail, Ban, Users, Lock, Edit, XCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const WHITE_LOGO_URL = "https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/c4f0c6sm_YBSLogoWhiteNoBack.png";

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/" className="p-2 rounded-xl hover:bg-secondary transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Terms & Conditions</h1>
            <p className="text-muted-foreground text-sm mt-1">Your Buy Site • Last updated: March 2026</p>
          </div>
        </div>

        {/* Section 1: Important Notice */}
        <div className="card p-6 mb-6 border-2 border-red-500/40 bg-gradient-to-r from-red-500/10 to-orange-500/10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-red-500 mb-3">1. Important Notice</h2>
              <p className="text-foreground mb-4">
                Your Buy Site ("Your Buy Site", "we", "us", "our") operates an online marketplace platform that connects buyers and sellers.
              </p>
              <p className="text-foreground font-semibold mb-2">We do NOT:</p>
              <ul className="space-y-1 text-muted-foreground mb-4">
                <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Process payments</li>
                <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Handle transactions</li>
                <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Store or ship goods</li>
                <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Act as a broker, agent, or intermediary</li>
              </ul>
              <p className="text-foreground font-bold text-red-500">
                All transactions are conducted directly between users at their own risk.
              </p>
            </div>
          </div>
        </div>

        {/* Section 2: Company Information */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">2. Company Information</h2>
          </div>
          <div className="flex items-center gap-4 p-4 bg-slate-800 rounded-xl">
            <img src={WHITE_LOGO_URL} alt="Your Buy Site" className="h-6 w-auto" />
            <div className="text-white">
              <p className="font-semibold">Your Buy Site</p>
              <p className="text-sm text-slate-300">Registered in Curaçao</p>
              <p className="text-sm text-slate-300">Caribbean Netherlands (ABC Islands: Aruba, Bonaire, Curaçao)</p>
            </div>
          </div>
        </div>

        {/* Section 3: Platform Role */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">3. Platform Role</h2>
          </div>
          <p className="text-muted-foreground mb-3">
            Your Buy Site is strictly a <strong className="text-foreground">technology platform</strong> that allows users to:
          </p>
          <ul className="space-y-2 text-muted-foreground mb-4">
            <li className="flex items-center gap-2">• List items for sale, rent, or trade</li>
            <li className="flex items-center gap-2">• Offer or request services</li>
          </ul>
          <p className="text-foreground font-medium">
            We are <strong>not a party</strong> to any agreement between users.
          </p>
        </div>

        {/* Section 4: User Eligibility */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">4. User Eligibility</h2>
          </div>
          <p className="text-muted-foreground mb-3">By using this platform, you confirm that:</p>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-center gap-2">• You are at least <strong className="text-foreground">18 years old</strong></li>
            <li className="flex items-center gap-2">• You have the legal capacity to enter into agreements</li>
            <li className="flex items-center gap-2">• You comply with all applicable laws and regulations</li>
          </ul>
        </div>

        {/* Section 5: User Accounts */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">5. User Accounts</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-amber-500/10 rounded-xl">
              <p className="font-semibold text-foreground mb-2">We reserve the right to:</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Suspend or terminate accounts at any time</li>
                <li>• Remove or edit listings without notice</li>
                <li>• Refuse service to any user</li>
              </ul>
            </div>
            <div className="p-4 bg-blue-500/10 rounded-xl">
              <p className="font-semibold text-foreground mb-2">You are responsible for:</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Maintaining account security</li>
                <li>• All activity under your account</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 6: Prohibited Use */}
        <div className="card p-6 mb-6 border-2 border-red-500/30">
          <div className="flex items-center gap-3 mb-4">
            <Ban className="w-6 h-6 text-red-500" />
            <h2 className="text-xl font-bold text-foreground">6. Prohibited Use</h2>
          </div>
          <p className="text-muted-foreground mb-3">Users may NOT use the platform for:</p>
          <ul className="space-y-2 text-muted-foreground mb-4">
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Illegal goods or services</li>
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Stolen or counterfeit items</li>
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Drugs, weapons, or restricted products</li>
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Fraud, scams, or misleading listings</li>
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Violations of local or international laws</li>
          </ul>
          <p className="text-foreground font-medium">
            We reserve the right to remove any content that violates these rules.
          </p>
        </div>

        {/* Section 7-9: No Responsibility */}
        <div className="card p-6 mb-6 border-2 border-amber-500/30">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-amber-500" />
            <h2 className="text-xl font-bold text-foreground">7-9. Liability Disclaimers</h2>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-red-500/10 rounded-xl border border-red-500/20">
              <h3 className="font-semibold text-red-500 mb-2">7. No Responsibility for Transactions</h3>
              <p className="text-muted-foreground text-sm mb-2">Your Buy Site is NOT responsible for:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Any agreements between users</li>
                <li>• Payment arrangements or transfers</li>
                <li>• Delivery or logistics</li>
                <li>• Returns, refunds, or disputes</li>
              </ul>
              <p className="text-foreground font-medium mt-2 text-sm">All transactions are solely between users.</p>
            </div>

            <div className="p-4 bg-red-500/10 rounded-xl border border-red-500/20">
              <h3 className="font-semibold text-red-500 mb-2">8. No Responsibility for Goods</h3>
              <p className="text-muted-foreground text-sm mb-2">We are NOT responsible for:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Quality, safety, or legality of items</li>
                <li>• Accuracy of listings or descriptions</li>
                <li>• Damaged, defective, or misrepresented goods</li>
                <li>• Condition of rented or traded items</li>
              </ul>
            </div>

            <div className="p-4 bg-red-500/10 rounded-xl border border-red-500/20">
              <h3 className="font-semibold text-red-500 mb-2">9. No Responsibility for Services</h3>
              <p className="text-muted-foreground text-sm mb-2">We are NOT responsible for:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Quality or outcome of services</li>
                <li>• Damages caused during services</li>
                <li>• Service-related disputes</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 10: Payment Disclaimer */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Wallet className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">10. Payment Disclaimer</h2>
          </div>
          <p className="text-foreground font-medium mb-3">
            Your Buy Site does NOT process or facilitate payments.
          </p>
          <p className="text-muted-foreground mb-2">We are NOT responsible for:</p>
          <ul className="space-y-1 text-muted-foreground">
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Payment disputes</li>
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Fraud or scams</li>
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Lost or delayed payments</li>
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Refunds or chargebacks</li>
          </ul>
        </div>

        {/* Section 11: Cryptocurrency Disclaimer */}
        <div className="card p-6 mb-6 border-2 border-emerald-500/30 bg-gradient-to-r from-emerald-500/5 to-teal-500/5">
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-6 h-6 text-emerald-500" viewBox="0 0 32 32" fill="currentColor">
              <path d="M16 0C7.163 0 0 7.163 0 16s7.163 16 16 16 16-7.163 16-16S24.837 0 16 0zm7.994 18.394c-.093.387-.307.68-.64.88-.333.2-.74.3-1.22.3H17.6v4.36h-3.2v-4.36H9.866c-.48 0-.887-.1-1.22-.3-.333-.2-.547-.493-.64-.88V17.1h13.988v1.294zm0-3.2H8.006v-1.28c.093-.4.307-.7.64-.9.333-.2.74-.3 1.22-.3H14.4V8.34h3.2v4.374h4.534c.48 0 .887.1 1.22.3.333.2.547.5.64.9v1.28z"/>
            </svg>
            <h2 className="text-xl font-bold text-foreground">11. Cryptocurrency Disclaimer (USDT / ERC-20)</h2>
          </div>
          <p className="text-muted-foreground mb-4">
            Users may share cryptocurrency wallet addresses for direct payments.
          </p>
          <p className="text-foreground font-medium mb-2">Your Buy Site does NOT:</p>
          <ul className="space-y-1 text-muted-foreground mb-4">
            <li>• Process crypto transactions</li>
            <li>• Verify wallet ownership</li>
            <li>• Store or manage funds</li>
          </ul>
          <p className="text-foreground font-medium mb-2">We are NOT responsible for:</p>
          <ul className="space-y-1 text-muted-foreground mb-4">
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Incorrect wallet addresses</li>
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Lost or irreversible transactions</li>
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Blockchain delays or failures</li>
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Price volatility</li>
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Any crypto-related disputes</li>
          </ul>
          <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
            <p className="text-sm text-amber-600 dark:text-amber-400 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span><strong>Warning:</strong> All blockchain transactions are irreversible. Users must verify all details before sending funds.</span>
            </p>
          </div>
        </div>

        {/* Section 12: Use at Your Own Risk */}
        <div className="card p-6 mb-6 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-2 border-amber-500/30">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            <h2 className="text-xl font-bold text-foreground">12. Use at Your Own Risk</h2>
          </div>
          <p className="text-muted-foreground mb-3">
            The platform is provided <strong className="text-foreground">"as is" and "as available"</strong>.
          </p>
          <p className="text-muted-foreground mb-2">By using Your Buy Site, you acknowledge that:</p>
          <ul className="space-y-2 text-muted-foreground">
            <li>• You assume full responsibility for all actions</li>
            <li>• You accept all risks associated with transactions</li>
            <li>• You use the platform at your own discretion</li>
          </ul>
        </div>

        {/* Section 13: Limitation of Liability */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Scale className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">13. Limitation of Liability</h2>
          </div>
          <p className="text-muted-foreground mb-3">To the maximum extent permitted by law:</p>
          <ul className="space-y-2 text-muted-foreground mb-4">
            <li>• Your Buy Site shall NOT be liable for any direct, indirect, incidental, or consequential damages</li>
            <li>• Including but not limited to: Loss of money, Loss of profits, Loss of data, Fraud or scams</li>
          </ul>
          <div className="p-3 bg-primary/10 rounded-lg">
            <p className="text-sm text-foreground font-medium">
              Our total liability, if any, is limited to the amount paid by the user to the platform in the last 12 months (which may be zero).
            </p>
          </div>
        </div>

        {/* Section 14: Indemnification */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">14. Indemnification</h2>
          </div>
          <p className="text-muted-foreground mb-3">
            You agree to <strong className="text-foreground">indemnify and hold harmless</strong> Your Buy Site, its directors, employees, and affiliates from:
          </p>
          <ul className="space-y-1 text-muted-foreground mb-3">
            <li>• Any claims</li>
            <li>• Legal actions</li>
            <li>• Damages or losses</li>
            <li>• Costs (including legal fees)</li>
          </ul>
          <p className="text-muted-foreground">Arising from: Your use of the platform, Your transactions with other users, Your violation of these Terms</p>
        </div>

        {/* Section 15: Disputes Between Users */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Gavel className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">15. Disputes Between Users</h2>
          </div>
          <p className="text-foreground font-medium mb-3">
            Your Buy Site is <strong>not involved in disputes</strong> between users.
          </p>
          <p className="text-muted-foreground mb-2">Users agree to:</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>• Resolve disputes directly between themselves</li>
            <li>• Not hold Your Buy Site responsible</li>
          </ul>
          <p className="text-muted-foreground mt-3 text-sm italic">
            We may assist at our discretion, but are not obligated to do so.
          </p>
        </div>

        {/* Section 16: Intellectual Property */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">16. Intellectual Property</h2>
          </div>
          <p className="text-muted-foreground mb-3">
            All platform content, branding, and technology are the property of Your Buy Site.
          </p>
          <p className="text-muted-foreground mb-2">Users may NOT:</p>
          <ul className="space-y-1 text-muted-foreground">
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Copy or reproduce the platform</li>
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Use branding without permission</li>
            <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Reverse engineer the system</li>
          </ul>
        </div>

        {/* Section 17-18: Modifications & Termination */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="card p-6">
            <div className="flex items-center gap-3 mb-4">
              <Edit className="w-6 h-6 text-primary" />
              <h2 className="text-lg font-bold text-foreground">17. Modifications</h2>
            </div>
            <p className="text-muted-foreground text-sm mb-2">We reserve the right to:</p>
            <ul className="space-y-1 text-muted-foreground text-sm">
              <li>• Modify these Terms at any time</li>
              <li>• Update platform features or policies</li>
            </ul>
            <p className="text-foreground text-sm mt-3 font-medium">
              Continued use of the platform means you accept any updates.
            </p>
          </div>

          <div className="card p-6">
            <div className="flex items-center gap-3 mb-4">
              <XCircle className="w-6 h-6 text-red-500" />
              <h2 className="text-lg font-bold text-foreground">18. Termination</h2>
            </div>
            <p className="text-muted-foreground text-sm mb-2">We may terminate or suspend access:</p>
            <ul className="space-y-1 text-muted-foreground text-sm">
              <li>• Without prior notice</li>
              <li>• For violations of these Terms</li>
              <li>• For any reason at our discretion</li>
            </ul>
          </div>
        </div>

        {/* Section 19: Governing Law */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">19. Governing Law</h2>
          </div>
          <p className="text-muted-foreground mb-2">
            These Terms are governed by the laws of <strong className="text-foreground">Curaçao</strong>.
          </p>
          <p className="text-muted-foreground">
            Any disputes shall be handled exclusively by the courts of <strong className="text-foreground">Curaçao</strong>.
          </p>
        </div>

        {/* Section 20: Contact */}
        <div className="card p-6 text-center">
          <Mail className="w-12 h-12 mx-auto mb-4 text-primary" />
          <h2 className="text-xl font-bold text-foreground mb-2">20. Contact</h2>
          <p className="text-muted-foreground mb-4">
            For questions regarding these Terms:
          </p>
          <div className="p-4 bg-slate-800 rounded-xl inline-block">
            <img src={WHITE_LOGO_URL} alt="Your Buy Site" className="h-5 w-auto mx-auto mb-2" />
            <p className="text-white font-semibold">Your Buy Site</p>
            <p className="text-slate-300 text-sm">Curaçao, Caribbean Netherlands</p>
          </div>
        </div>

        {/* Agreement Notice */}
        <div className="mt-8 p-4 bg-secondary rounded-xl text-center">
          <p className="text-sm text-muted-foreground">
            By using Your Buy Site, you acknowledge that you have read, understood, and agree to be bound 
            by these Terms & Conditions. If you do not agree, please do not use our platform.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsPage;
